import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import { Notice, Tag } from '../../../common/components/detail/detail';
import { renderMarkdown } from '../../../common/components/markdown/markdown';
import { formatDateTime } from '../../../core/utils/date';
import { useLatestVersion, useProjectDocuments } from '../contexts/documents';
import { parseOpenApi } from '../utils/openapi';
import OpenApiViewer from './openapi-viewer';

// A project's API reference: its `api_reference` document, at its latest version.
//
// A PROJECT CAN HAVE MORE THAN ONE — a backend and a public API, say — so they
// are offered by title and the chosen one travels in `?doc=`. With one, there is
// nothing to choose.
//
// WHAT THE TEXT IS decides how it is drawn: an OpenAPI description in JSON gets
// the structured reference, anything else is the prose somebody wrote and is
// rendered as Markdown. A reference is never invented from a text that is not
// one.

export default function ApiReferenceTab({ projectId }) {
    const [params, setParams] = useSearchParams();
    const { data: documents = [], isLoading, error } = useProjectDocuments(projectId, 'api_reference');

    if (!projectId) {
        return (
            <Notice>
                Elegí un proyecto para ver su API reference. Cada proyecto documenta su propia API.
            </Notice>
        );
    }

    if (isLoading) return <p className="documentation__state">Buscando la documentación del proyecto...</p>;

    if (error) {
        return (
            <p className="documentation__state documentation__state--error">
                {error.message || 'No se pudieron leer los documentos del proyecto'}
            </p>
        );
    }

    if (!documents.length) {
        return (
            <Notice>
                Este proyecto todavía no tiene un documento de tipo <strong>API reference</strong>.
                Se crea en Core como documento del proyecto con <code>kind: api_reference</code>, y
                su contenido puede ser un OpenAPI en JSON o texto en Markdown.
            </Notice>
        );
    }

    const wanted = params.get('doc');
    const current = documents.find(item => String(item.id) === wanted) ?? documents[0];

    const choose = (id) => {
        const target = new URLSearchParams(params);
        target.set('doc', String(id));
        ['q', 'method', 'tag'].forEach(key => target.delete(key));
        setParams(target, { replace: true });
    };

    return (
        <div className="documentation__doc">
            {documents.length > 1 && (
                <div className="documentation__docs" role="group" aria-label="Documentos de API del proyecto">
                    {documents.map(item => (
                        <button
                            key={item.id}
                            type="button"
                            className={`documentation__doc-pick ${item.id === current.id ? 'documentation__doc-pick--on' : ''}`}
                            onClick={() => choose(item.id)}
                            aria-pressed={item.id === current.id}
                        >
                            {item.title}
                        </button>
                    ))}
                </div>
            )}

            <DocumentBody document={current} />
        </div>
    );
}

function DocumentBody({ document }) {
    const { data: version, isLoading, error } = useLatestVersion(document.id);
    const published = document.doc_state === 'published';

    // Parsed once per version: an OpenAPI can weigh hundreds of KB.
    const spec = useMemo(() => (version ? parseOpenApi(version.content) : null), [version]);

    return (
        <>
            <header className="documentation__doc-head">
                <h3 className="documentation__doc-title">{document.title}</h3>
                <div className="documentation__doc-tags">
                    {/* A draft is readable and said to be one: the agents'
                        context package only trusts what was published. */}
                    <Tag tone={published ? 'active' : 'warn'}>{published ? 'Publicado' : 'Borrador'}</Tag>
                    {version && <Tag>Versión {version.number}</Tag>}
                    {(version?.created_at ?? version?.createdAt) && <Tag>{formatDateTime(version.created_at ?? version.createdAt)}</Tag>}
                    {version && <Tag>{spec ? 'OpenAPI' : 'Texto'}</Tag>}
                </div>
            </header>

            {isLoading && <p className="documentation__state">Leyendo la última versión...</p>}

            {error && (
                <p className="documentation__state documentation__state--error">
                    {error.message || 'No se pudo leer la versión del documento'}
                </p>
            )}

            {!isLoading && !error && !version && (
                <Notice>
                    El documento existe pero todavía no tiene ninguna versión escrita: no hay texto
                    que mostrar.
                </Notice>
            )}

            {version && (spec
                ? <OpenApiViewer spec={spec} />
                : (
                    <article className="md documentation__prose">
                        {renderMarkdown(version.content) ?? (
                            <p className="documentation__state">La versión no tiene contenido.</p>
                        )}
                    </article>
                ))}
        </>
    );
}
